<script setup lang="ts">
import { hasText } from '~/utils/hasText'
import { BellIcon, ChevronRightIcon } from '@heroicons/vue/24/outline'
import { BellRing, CheckCircle, CheckIcon, CircleX, ShoppingBag, TriangleAlert } from '@lucide/vue'
import { computed, nextTick, onBeforeUnmount, ref, watch, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseDrawer from '~/components/shared/dialogs/BaseDrawer.vue'
import { OrderTypeSchema } from '~/services/api/generated'
import type { NotificationInboxDto } from '~/services/api/generated'
import { OrderTypePresentation } from '~/presentation/OrderTypePresentation'
import { formatRelativeTime } from '~/utils/formatRelativeTime'
import { resolveNotificationDisplay } from '~/composables/resolveNotificationDisplay'
import type { NotificationDisplay } from '~/composables/NotificationDisplay'
import OrderCountdownBadge from '~/components/OrderCountdownBadge.vue'
import type { DashboardQuickAction } from '~/composables/DashboardQuickAction'

type NotificationInboxItem = NotificationInboxDto

const props = withDefaults(
  defineProps<{
    /** Notifications currently available in the drawer. */
    items: NotificationInboxItem[]
    /** Number of unread notifications shown in the trigger badge. */
    unreadCount: number
    /** Whether the initial notification load has completed. */
    isLoaded: boolean
    /** Controls whether the notification drawer is open. */
    modelValue: boolean
    /** Whether the realtime notification connection is reconnecting. */
    isConnecting: boolean
    /** Whether another page of notifications is loading. */
    isFetchingMore: boolean
    /** Whether a notification deletion request is in progress. */
    isDeleting: boolean
    /** Identifier of the notification deletion request that most recently succeeded. */
    deleteSuccessKey: number
    /** Whether more notifications are available to load. */
    hasNextPage: boolean
    /** Timestamp of the most recently received notification, when available. */
    lastReceivedAt: string | null
    /** Error message from notification loading, when one exists. */
    error: string | null
    /** Additional classes applied to the notification trigger button. */
    triggerButtonClass?: string
    /** Additional classes applied to the notification trigger icon. */
    triggerIconClass?: string
    /** Additional classes applied to the unread-count badge. */
    triggerBadgeClass?: string
    /** Critical and high-priority onboarding work shown ahead of inbox history. */
    onboardingTasks?: DashboardQuickAction[]
  }>(),
  {
    onboardingTasks: () => [],
    triggerBadgeClass: '',
    triggerButtonClass: '',
    triggerIconClass: ''
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  notificationSelected: [notification: NotificationInboxItem]
  markAllRead: []
  delete: [notificationIds: string[]]
  loadMore: []
  selectOnboardingTask: [task: DashboardQuickAction]
}>()

const { t } = useI18n()

const openNotifications = () => {
  emit('update:modelValue', true)
}

const handleModelValueUpdate = (value: boolean) => {
  emit('update:modelValue', value)
}

const handleMarkAllRead = () => {
  emit('markAllRead')
}

const listRef = useTemplateRef('listRef')
const sentinelRef = useTemplateRef('sentinelRef')
const isBellRinging = ref(false)
const isDeleteMode = ref(false)
const selectedIds = ref<string[]>([])

let bellAnimationTimeout: ReturnType<typeof setTimeout> | null = null
let loadMoreObserver: IntersectionObserver | null = null

const attentionCount = computed(() => props.unreadCount + props.onboardingTasks.length)
const isFullyCaughtUp = computed(() => attentionCount.value === 0)
const badgeLabel = computed(() => (attentionCount.value > 9 ? '+9' : String(attentionCount.value)))
const currentBellIcon = computed(() => (isBellRinging.value ? BellRing : BellIcon))
const triggerButtonClasses = computed(() => [
  hasText(props.triggerButtonClass)
    ? props.triggerButtonClass
    : 'relative -m-2.5 inline-flex items-center justify-center rounded-full p-2.5 text-content-level-400 transition-colors duration-300 hover:bg-surface-level-100 hover:text-content-level-600 dark:hover:bg-surface-canvas/5 dark:hover:text-content-inverse cursor-pointer',
  {
    'animate-pulse bg-primary/10 text-primary dark:bg-primary/15 dark:text-primary':
      isBellRinging.value
  }
])
const triggerIconClass = computed(() =>
  hasText(props.triggerIconClass) ? props.triggerIconClass : 'size-6'
)
const triggerBadgeClass = computed(() =>
  hasText(props.triggerBadgeClass)
    ? props.triggerBadgeClass
    : 'absolute -right-0.5 top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[12px] font-semibold leading-none text-content-inverse shadow-sm'
)
const statusLabel = computed(() => {
  if (props.isConnecting) {
    return t('dashboard.messages.reconnectingLiveUpdates')
  }

  if (props.onboardingTasks.length > 0 && props.unreadCount > 0) {
    return t('dashboard.messages.notificationsAndOnboardingStatus', {
      taskcount: props.onboardingTasks.length,
      unreadcount: props.unreadCount
    })
  }

  if (props.onboardingTasks.length > 0) {
    return t('dashboard.messages.pendingOnboardingTaskCount', {
      count: props.onboardingTasks.length
    })
  }

  if (props.unreadCount > 0) {
    return t('dashboard.messages.unreadNotificationsCount', { count: props.unreadCount })
  }

  return t('dashboard.messages.allCaughtUp')
})
const triggerAccessibleLabel = computed(() => {
  if (isFullyCaughtUp.value) return t('dashboard.actions.viewNotifications')

  if (props.onboardingTasks.length > 0 && props.unreadCount > 0) {
    return t('dashboard.messages.notificationBellAttentionSummary', {
      taskcount: props.onboardingTasks.length,
      unreadcount: props.unreadCount
    })
  }

  if (props.onboardingTasks.length > 0) {
    return t('dashboard.messages.pendingOnboardingTaskCount', {
      count: props.onboardingTasks.length
    })
  }

  return t('dashboard.messages.unreadNotificationsCount', { count: props.unreadCount })
})

const shouldObserveLoadMore = computed(
  () =>
    props.modelValue &&
    props.isLoaded &&
    !hasText(props.error) &&
    props.hasNextPage &&
    !props.isFetchingMore &&
    props.items.length > 0
)
const hasItems = computed(() => props.items.length > 0)
const canShowClearButton = computed(() => hasItems.value && !isDeleteMode.value)
const selectedIdsSet = computed(() => new Set(selectedIds.value))
const selectedCount = computed(() => selectedIds.value.length)
const allVisibleIds = computed(() => props.items.map((item) => item.id))
const allVisibleSelected = computed(
  () => hasItems.value && selectedCount.value > 0 && selectedCount.value === props.items.length
)

const formatRelativeTimestamp = (value: string | null) => formatRelativeTime(value) || 'Just now'

const formatCurrency = (value: unknown) => {
  const amount = Number(value)

  if (!Number.isFinite(amount)) {
    return null
  }

  return new Intl.NumberFormat('en-US', {
    currency: 'USD',
    style: 'currency'
  }).format(amount)
}

// Glyph reflects severity first, falling back to entity type, then a generic bell.
const resolveIcon = (notification: NotificationInboxItem) => {
  if (notification.severity === 'danger') return CircleX
  if (notification.severity === 'success') return CheckCircle
  if (notification.severity === 'warning') return TriangleAlert
  if (notification.entity_type === 'order') return ShoppingBag
  return BellIcon
}

const hasAction = (notification: NotificationInboxItem) => Boolean(notification.action_url)

const notificationDisplayById = computed(() => {
  const displayById = new Map<string, NotificationDisplay>()

  for (const notification of props.items) {
    displayById.set(notification.id, resolveNotificationDisplay(notification, t))
  }

  return displayById
})

const notificationDisplay = (notification: NotificationInboxItem): NotificationDisplay =>
  notificationDisplayById.value.get(notification.id) ?? resolveNotificationDisplay(notification, t)

const notificationTitle = (notification: NotificationInboxItem) =>
  notificationDisplay(notification).title

const notificationBody = (notification: NotificationInboxItem) =>
  notificationDisplay(notification).body

const notificationActionLabel = (notification: NotificationInboxItem) =>
  notificationDisplay(notification).actionLabel

const metaValue = (
  notification: NotificationInboxItem,
  key: keyof NotificationInboxItem['meta']
) => {
  const value = notification.meta[key]

  return typeof value === 'string' || typeof value === 'number' ? String(value) : null
}

const notificationOrderType = (notification: NotificationInboxItem): string | null => {
  const orderType = OrderTypeSchema.safeParse(metaValue(notification, 'order_type'))
  return orderType.success ? OrderTypePresentation[orderType.data].label : null
}

const notificationContext = (notification: NotificationInboxItem) => {
  const orderNumber = metaValue(notification, 'order_number')
  const storeName = metaValue(notification, 'store_name')

  if (hasText(orderNumber) && hasText(storeName)) {
    return `Order #${orderNumber} · ${storeName}`
  }

  return hasText(storeName) ? storeName : orderNumber
}

const shouldShowContext = (notification: NotificationInboxItem) =>
  !hasText(notificationBody(notification)) && hasText(notificationContext(notification))

const notificationTotal = (notification: NotificationInboxItem) => {
  return formatCurrency(notification.meta['total'])
}

const triggerBellAnimation = () => {
  isBellRinging.value = true

  if (bellAnimationTimeout) {
    clearTimeout(bellAnimationTimeout)
  }

  bellAnimationTimeout = setTimeout(() => {
    isBellRinging.value = false
    bellAnimationTimeout = null
  }, 4000)
}

const enterDeleteMode = () => {
  isDeleteMode.value = true
}

const exitDeleteMode = () => {
  isDeleteMode.value = false
  selectedIds.value = []
}

const toggleSelection = (notificationId: string) => {
  if (!isDeleteMode.value) {
    return
  }

  if (selectedIdsSet.value.has(notificationId)) {
    selectedIds.value = selectedIds.value.filter((id) => id !== notificationId)
    return
  }

  selectedIds.value = [...selectedIds.value, notificationId]
}

const selectAllVisible = () => {
  selectedIds.value = [...allVisibleIds.value]
}

const toggleAllVisibleSelection = () => {
  if (allVisibleSelected.value) {
    selectedIds.value = []
    return
  }

  selectAllVisible()
}

const requestDeleteSelected = () => {
  if (selectedIds.value.length === 0 || props.isDeleting) {
    return
  }

  emit('delete', selectedIds.value)
}

const disconnectObserver = () => {
  if (loadMoreObserver === null) {
    return
  }

  loadMoreObserver.disconnect()
  loadMoreObserver = null
}

const setupObserver = async () => {
  disconnectObserver()

  if (import.meta.server || !shouldObserveLoadMore.value) {
    return
  }

  await nextTick()

  if (listRef.value == null || sentinelRef.value == null) {
    return
  }

  loadMoreObserver = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return
      }

      emit('loadMore')
    },
    {
      root: listRef.value,
      rootMargin: '0px 0px 160px 0px',
      threshold: 0.1
    }
  )

  loadMoreObserver.observe(sentinelRef.value)
}

watch(
  () => props.lastReceivedAt,
  (receivedAt, previousReceivedAt) => {
    if (receivedAt === null || receivedAt === previousReceivedAt) {
      return
    }

    triggerBellAnimation()
  }
)

watch(
  () => props.deleteSuccessKey,
  (nextKey, previousKey) => {
    if (nextKey !== previousKey && nextKey > 0) {
      exitDeleteMode()
    }
  }
)

watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) {
      exitDeleteMode()
    }
  }
)

watch(
  () => [props.items, props.isDeleting] as const,
  ([items, deleting], [previousItems, previousDeleting]) => {
    if (!isDeleteMode.value) {
      return
    }

    const availableIds = new Set(items.map((item) => item.id))
    selectedIds.value = selectedIds.value.filter((id) => availableIds.has(id))

    if (items.length === 0) {
      exitDeleteMode()
      return
    }

    if (previousDeleting && !deleting && previousItems.length > items.length) {
      exitDeleteMode()
    }
  }
)

watch(
  () => [
    props.modelValue,
    props.hasNextPage,
    props.isFetchingMore,
    props.isLoaded,
    props.error,
    props.items.length
  ],
  () => {
    void setupObserver()
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  disconnectObserver()

  if (bellAnimationTimeout !== null) {
    clearTimeout(bellAnimationTimeout)
    bellAnimationTimeout = null
  }
})
</script>

<template>
  <div class="relative">
    <button
      type="button"
      :class="triggerButtonClasses"
      :aria-label="triggerAccessibleLabel"
      @click="openNotifications"
    >
      <component
        :is="currentBellIcon"
        :class="triggerIconClass"
        aria-hidden="true"
      />

      <span
        v-if="attentionCount > 0"
        :class="triggerBadgeClass"
        aria-hidden="true"
      >
        {{ badgeLabel }}
      </span>
    </button>

    <BaseDrawer
      :model-value="modelValue"
      :blur-backdrop="false"
      full-screen-on-mobile
      :title="$t('dashboard.labels.notifications')"
      :description="$t('dashboard.messages.liveAlertsAndImportantAccountUpdates')"
      max-width-class="max-w-xl"
      @update:model-value="handleModelValueUpdate"
    >
      <div class="-mx-5 -my-5 bg-surface-canvas dark:bg-surface-level-900 flex flex-col h-full">
        <div class="pb-3 pt-5 px-5">
          <div class="flex gap-4 items-start justify-between">
            <div class="min-w-0">
              <div class="flex flex-wrap gap-2 items-center">
                <span
                  v-if="attentionCount > 0 && !isDeleteMode"
                  class="border border-border-level-200 dark:border-border-inverse/10 dark:text-content-level-300 font-medium inline-flex items-center px-2.5 py-1 rounded-full text-[11px] text-content-level-500"
                >
                  {{ statusLabel }}
                </span>

                <span
                  v-if="isDeleteMode"
                  class="border border-border-level-200 dark:border-border-inverse/10 dark:text-content-level-300 font-medium inline-flex items-center px-2.5 py-1 rounded-full text-[11px] text-content-level-500"
                  >{{
                    $t('dashboard.messages.selectedcountSelected', { selectedcount: selectedCount })
                  }}</span
                >
              </div>
            </div>

            <div
              v-if="isDeleteMode"
              class="bg-surface-canvas border border-border-level-200 dark:bg-surface-canvas/[0.03] dark:border-border-inverse/10 inline-flex items-center overflow-hidden rounded-xl shadow-xs shrink-0"
            >
              <button
                v-if="items.length"
                type="button"
                class="border-border-level-200 border-r cursor-pointer dark:border-border-inverse/10 dark:hover:bg-surface-canvas/5 dark:text-content-level-200 font-semibold hover:bg-surface-level-50 inline-flex items-center px-3.5 py-2 text-content-level-600 text-xs transition"
                @click="toggleAllVisibleSelection"
              >
                {{
                  allVisibleSelected
                    ? $t('dashboard.actions.clearSelection')
                    : $t('dashboard.actions.selectAll')
                }}
              </button>

              <button
                type="button"
                class="border-border-level-200 border-r cursor-pointer dark:border-border-inverse/10 dark:hover:bg-surface-canvas/5 disabled:cursor-not-allowed disabled:opacity-60 font-semibold hover:bg-surface-level-50 inline-flex items-center px-3.5 py-2 text-danger-400 text-xs transition"
                :disabled="!selectedCount || isDeleting"
                @click="requestDeleteSelected"
              >
                {{
                  isDeleting
                    ? $t('dashboard.messages.deleting')
                    : $t('dashboard.actions.deleteSelected')
                }}
              </button>

              <button
                type="button"
                class="cursor-pointer dark:hover:bg-surface-canvas/5 dark:text-content-level-200 disabled:cursor-not-allowed disabled:opacity-60 font-semibold hover:bg-surface-level-50 inline-flex items-center px-3.5 py-2 text-content-level-600 text-xs transition"
                :disabled="isDeleting"
                @click="exitDeleteMode"
              >
                {{ $t('dashboard.actions.cancel') }}
              </button>
            </div>

            <div
              v-else
              class="flex gap-2 items-center shrink-0"
            >
              <button
                v-if="unreadCount > 0"
                type="button"
                class="border border-primary/45 cursor-pointer font-semibold hover:bg-primary/25 inline-flex items-center px-3 py-1.5 rounded-full text-primary text-xs transition"
                @click="handleMarkAllRead"
              >
                {{ $t('dashboard.labels.markAllRead') }}
              </button>

              <span
                v-else-if="isFullyCaughtUp"
                class="border border-border-level-200 dark:border-border-inverse/10 dark:text-content-level-300 font-medium inline-flex items-center px-2.5 py-1 rounded-full text-[11px] text-content-level-500"
              >
                {{ statusLabel }}
              </span>

              <button
                v-if="canShowClearButton"
                type="button"
                class="border border-border-level-200 cursor-pointer dark:border-border-inverse/10 dark:hover:bg-surface-canvas/5 dark:text-content-level-200 font-semibold hover:bg-surface-level-50 inline-flex items-center px-3 py-1.5 rounded-full text-content-level-600 text-xs transition"
                @click="enterDeleteMode"
              >
                {{ $t('dashboard.labels.clearNotifications') }}
              </button>
            </div>
          </div>
        </div>

        <section
          v-if="onboardingTasks.length > 0"
          class="border-border-level-200 border-t dark:border-border-inverse/10 px-5 py-4"
          aria-labelledby="onboarding-notification-tasks-title"
        >
          <div class="mb-3">
            <h3
              id="onboarding-notification-tasks-title"
              class="dark:text-content-inverse font-semibold text-content-level-900 text-sm"
            >
              {{ $t('dashboard.labels.onboardingTasksNeedAttention') }}
            </h3>

            <p class="dark:text-content-level-400 mt-1 text-content-level-500 text-xs">
              {{ $t('dashboard.messages.urgentOnboardingTasksDescription') }}
            </p>
          </div>

          <div class="space-y-2">
            <button
              v-for="task in onboardingTasks"
              :key="task.id"
              type="button"
              class="border border-border-level-200 cursor-pointer dark:border-border-inverse/10 dark:hover:bg-surface-canvas/5 flex gap-3 hover:bg-surface-level-50 items-center p-3 rounded-xl text-left transition w-full"
              @click="() => emit('selectOnboardingTask', task)"
            >
              <span
                class="font-semibold px-2 py-0.5 rounded-full shrink-0 text-[10px] tracking-wide uppercase"
                :class="
                  task.priority === 'critical'
                    ? 'bg-danger-50 text-danger-700 dark:bg-danger-900/20 dark:text-danger-400'
                    : 'bg-warning-50 text-warning-700 dark:bg-warning-900/20 dark:text-warning-400'
                "
              >
                {{
                  task.priority === 'critical'
                    ? $t('dashboard.labels.criticalPriority')
                    : $t('dashboard.labels.highPriority')
                }}
              </span>

              <span class="flex-1 min-w-0">
                <span
                  class="block dark:text-content-inverse font-semibold text-content-level-900 text-sm"
                >
                  {{ task.title }}
                </span>

                <span
                  class="block dark:text-content-level-400 line-clamp-2 mt-0.5 text-content-level-500 text-xs"
                >
                  {{ task.description }}
                </span>
              </span>

              <ChevronRightIcon
                class="dark:text-content-level-500 shrink-0 size-4 text-content-level-400"
                aria-hidden="true"
              />
            </button>
          </div>
        </section>

        <div
          v-if="!isLoaded && !error"
          class="px-5 py-5 space-y-3"
        >
          <div
            v-for="index in 5"
            :key="index"
            class="border border-border-level-200/80 dark:border-border-inverse/10 overflow-hidden p-4 rounded-xl"
          >
            <div class="flex gap-3 items-start">
              <div
                class="animate-pulse bg-surface-level-200 dark:bg-surface-canvas/10 rounded-lg size-11"
              />

              <div class="flex-1 min-w-0 space-y-2">
                <div
                  class="animate-pulse bg-surface-level-200 dark:bg-surface-canvas/10 h-4 rounded-full w-2/5"
                />

                <div
                  class="animate-pulse bg-surface-level-100 dark:bg-surface-canvas/5 h-3 rounded-full w-11/12"
                />

                <div
                  class="animate-pulse bg-surface-level-100 dark:bg-surface-canvas/5 h-3 rounded-full w-8/12"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          v-else-if="error"
          class="px-5 py-10 text-center"
        >
          <div
            class="bg-warning-50/80 border border-warning-200/80 dark:bg-warning-500/10 dark:border-warning-500/20 px-5 py-6 rounded-xl"
          >
            <p class="dark:text-content-inverse font-semibold text-content-level-900 text-sm">
              {{ $t('dashboard.labels.inboxUnavailable') }}
            </p>

            <p class="dark:text-content-level-300 mt-2 text-content-level-600 text-sm">
              {{ error }}
            </p>
          </div>
        </div>

        <div
          v-else-if="items.length"
          ref="listRef"
          class="flex-1 min-h-0 overflow-y-auto px-4 py-4"
        >
          <div class="space-y-3">
            <article
              v-for="notification in items"
              :key="notification.id"
              class="border duration-200 ease-out p-4 rounded-xl transition-colors"
              :class="[
                notification.is_read
                  ? 'border-border-level-200/80 bg-surface-canvas hover:border-border-level-300 hover:bg-surface-level-50 dark:border-border-inverse/10 dark:bg-surface-clear dark:hover:border-border-inverse/15 dark:hover:bg-surface-canvas/[0.03]'
                  : 'border-primary/20 bg-primary/5 hover:border-primary/30 hover:bg-primary/7 dark:border-primary/25 dark:bg-primary/10 dark:hover:bg-primary/12',
                isDeleteMode ? 'cursor-pointer' : '',
                selectedIdsSet.has(notification.id) ? '!border-primary' : ''
              ]"
              @click="() => toggleSelection(notification.id)"
            >
              <div class="flex gap-3.5 items-start relative">
                <button
                  v-if="isDeleteMode"
                  type="button"
                  class="-left-6 -top-6 absolute border cursor-pointer duration-200 ease-out inline-flex items-center justify-center mt-0.5 rounded-full shrink-0 size-5 transition-colors"
                  :class="
                    selectedIdsSet.has(notification.id)
                      ? 'border-primary bg-primary text-content-inverse'
                      : 'border-border-level-300 bg-surface-canvas text-content-clear dark:border-border-inverse/15 dark:bg-surface-level-900'
                  "
                  @click.stop="() => toggleSelection(notification.id)"
                >
                  <CheckIcon
                    class="duration-200 ease-out size-3.5 transition-all"
                    :class="
                      selectedIdsSet.has(notification.id)
                        ? 'scale-100 opacity-100'
                        : 'scale-50 opacity-0'
                    "
                    aria-hidden="true"
                  />

                  <span class="sr-only">
                    {{
                      selectedIdsSet.has(notification.id)
                        ? $t('dashboard.actions.deselectNotification')
                        : $t('dashboard.actions.selectNotification')
                    }}
                  </span>
                </button>

                <div
                  class="bg-primary-50 dark:bg-primary/30 dark:text-primary-400 flex items-center justify-center mt-0.5 rounded-lg shrink-0 size-11 text-primary-600"
                >
                  <component
                    :is="resolveIcon(notification)"
                    class="size-5"
                    aria-hidden="true"
                  />
                </div>

                <div class="flex-1 min-w-0">
                  <div class="flex gap-3 items-start justify-between">
                    <div class="min-w-0">
                      <p
                        class="dark:text-content-inverse font-semibold leading-5 text-content-level-900 text-sm"
                      >
                        {{ notificationTitle(notification) }}
                      </p>

                      <OrderCountdownBadge
                        v-if="notification.expires_at"
                        :deadline="notification.expires_at"
                        class="mt-1.5"
                      />

                      <p
                        v-if="shouldShowContext(notification)"
                        class="dark:text-content-level-400 mt-1 text-content-level-500 text-xs"
                      >
                        {{ notificationContext(notification) }}
                      </p>
                    </div>

                    <div class="shrink-0 text-right">
                      <p
                        class="dark:text-content-level-500 mt-1 text-[11px] text-content-level-400"
                      >
                        {{ formatRelativeTimestamp(notification.created_at) }}
                      </p>
                    </div>
                  </div>

                  <p
                    v-if="notificationBody(notification)"
                    class="dark:text-content-level-300 leading-6 mt-3 text-content-level-600 text-sm"
                  >
                    {{ notificationBody(notification) }}
                  </p>

                  <div class="flex flex-wrap gap-3 items-center justify-between mt-3">
                    <div
                      class="dark:text-content-level-400 flex flex-wrap gap-x-3 gap-y-1 items-center text-content-level-500 text-xs"
                    >
                      <span
                        v-if="notificationTotal(notification)"
                        class="dark:text-content-level-200 font-medium text-content-level-700"
                      >
                        {{ notificationTotal(notification) }}
                      </span>

                      <span
                        v-if="
                          notificationTotal(notification) && notificationOrderType(notification)
                        "
                        class="dark:text-content-level-500 text-content-level-400"
                        aria-hidden="true"
                        >•</span
                      >

                      <span v-if="notificationOrderType(notification)">
                        {{ notificationOrderType(notification) }}
                      </span>

                      <span
                        v-if="
                          (notificationTotal(notification) ||
                            notificationOrderType(notification)) &&
                          metaValue(notification, 'customer_name')
                        "
                        class="dark:text-content-level-500 text-content-level-400"
                        aria-hidden="true"
                      >
                        •
                      </span>

                      <span v-if="metaValue(notification, 'customer_name')">
                        {{ metaValue(notification, 'customer_name') }}
                      </span>
                    </div>

                    <button
                      v-if="hasAction(notification) && !isDeleteMode"
                      type="button"
                      class="bg-primary border border-primary/20 cursor-pointer font-semibold gap-2 hover:bg-primary/90 inline-flex items-center px-2 py-1.5 rounded-md text-content-inverse text-xs transition"
                      @click="() => emit('notificationSelected', notification)"
                    >
                      <span>{{
                        notificationActionLabel(notification) ||
                        $t('dashboard.actions.openNotification')
                      }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </article>
          </div>

          <div
            v-if="isFetchingMore"
            class="dark:text-content-level-400 px-3 py-5 text-center text-content-level-500 text-sm"
          >
            {{ $t('dashboard.messages.loadingMoreFromYourInbox') }}
          </div>

          <div
            ref="sentinelRef"
            class="h-1 w-full"
            aria-hidden="true"
          />
        </div>

        <div
          v-else
          class="px-5 py-10"
        >
          <div
            class="border border-border-level-200 border-dashed dark:border-border-inverse/10 px-6 py-10 rounded-xl text-center"
          >
            <div
              class="border border-primary/50 flex items-center justify-center mx-auto rounded-xl size-14 text-primary"
            >
              <BellIcon
                class="size-6"
                aria-hidden="true"
              />
            </div>

            <p
              class="dark:text-content-inverse font-semibold mt-4 text-base text-content-level-900"
            >
              {{ $t('dashboard.labels.noNotificationsYet') }}
            </p>

            <p class="dark:text-content-level-400 leading-6 mt-2 text-content-level-500 text-sm">
              {{
                $t(
                  'dashboard.messages.liveAlertsOrderActivityAndInboxHistoryWillAppearHereOnceSomethingNewComesIn'
                )
              }}
            </p>
          </div>
        </div>
      </div>
    </BaseDrawer>
  </div>
</template>
